import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useSocketEvent } from '../context/SocketContext';
import DatePicker from '../components/DatePicker';
import ConfirmDialog from '../components/ConfirmDialog';
import { Plus, Trash2, Wallet, Calendar } from 'lucide-react';
import { todayISO } from '../utils/clinicDate';

function fmt(n) {
  return Math.round(n || 0).toLocaleString('en-IN');
}

// Remember the last selected date until the user manually changes it —
// same persistence pattern used on Lab/Billing/Pharmacy.
const EXPENSES_DATE_KEY = 'expenses_selectedDate';

export default function ExpensesPage() {
  const { showToast } = useToast();

  const [selectedDate, setSelectedDateRaw] = useState(() => localStorage.getItem(EXPENSES_DATE_KEY) || 'all');
  function setSelectedDate(d) {
    setSelectedDateRaw(d);
    localStorage.setItem(EXPENSES_DATE_KEY, d);
  }

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ note: '', amount: '', method: 'cash', date: todayISO() });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/expenses', { params: { date: selectedDate } });
      setExpenses(res.data.expenses || []);
    } catch (err) {
      showToast('Could not load expenses.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, showToast]);

  useEffect(() => { load(); }, [load]);
  useSocketEvent('expense:created', load);
  useSocketEvent('expense:deleted', load);

  async function addExpense() {
    if (!form.note.trim()) { showToast('Enter a description.', 'error'); return; }
    if (!form.amount || Number(form.amount) <= 0) { showToast('Enter an amount greater than 0.', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/expenses', {
        note: form.note.trim(),
        amount: Number(form.amount),
        method: form.method,
        date: form.date || todayISO(),
      });
      showToast('Expense recorded.', 'success');
      setForm({ note: '', amount: '', method: 'cash', date: todayISO() });
      setShowAddForm(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not record expense.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/expenses/${deleteTarget._id}`);
      showToast('Expense deleted.', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete this expense.', 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const todayTotal = expenses
    .filter(e => e.date === todayISO())
    .reduce((s, e) => s + (e.amount || 0), 0);

  // Group by date so "today" and "previous" days are visually distinct,
  // instead of one flat list mixing every day together.
  const groups = {};
  expenses.forEach(e => { (groups[e.date] = groups[e.date] || []).push(e); });
  const sortedDates = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1)); // newest first

  return (
    <AppLayout title="Expenses" subtitle="Today's and previous expenses, in one place">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Calendar size={15} color="var(--color-ink-faint)" />
          <DatePicker
            selectedDate={selectedDate}
            onChange={setSelectedDate}
            availableDates={new Set()}
          />
          <button className="btn btn-outline btn-sm" onClick={() => setSelectedDate(todayISO())}>
            Today
          </button>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(v => !v)}>
          <Plus size={14} /> {showAddForm ? 'Close' : 'Add Expense'}
        </button>
      </div>

      {showAddForm && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label className="label">Description</label>
              <input className="input" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Dental supplies" />
            </div>
            <div className="field" style={{ width: 120, marginBottom: 0 }}>
              <label className="label">Amount (₹)</label>
              <input className="input" type="number" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
            </div>
            <div className="field" style={{ width: 130, marginBottom: 0 }}>
              <label className="label">Method</label>
              <select className="select" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>
            <div className="field" style={{ width: 150, marginBottom: 0 }}>
              <label className="label">Date</label>
              <input className="input" type="date" max={todayISO()} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={addExpense} disabled={saving} style={{ marginBottom: 0 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div className="card lift3d-card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontWeight: 700, textTransform: 'uppercase' }}>
            {selectedDate === 'all' ? 'Total (all time)' : `Total — ${selectedDate}`}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>₹{fmt(total)}</div>
        </div>
        <div className="card lift3d-card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontWeight: 700, textTransform: 'uppercase' }}>Today's Expenses</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--color-danger)' }}>₹{fmt(todayTotal)}</div>
        </div>
        <div className="card lift3d-card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontWeight: 700, textTransform: 'uppercase' }}>Entries Shown</div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{expenses.length}</div>
        </div>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 200, borderRadius: 10 }} />
      ) : expenses.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
          <Wallet size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
          <div>No expenses recorded {selectedDate === 'all' ? 'yet' : `for ${selectedDate}`}.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {sortedDates.map(date => {
            const dayExpenses = groups[date];
            const dayTotal = dayExpenses.reduce((s, e) => s + (e.amount || 0), 0);
            const isToday = date === todayISO();
            return (
              <div key={date} className="card" style={{ overflow: 'hidden' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', background: isToday ? 'var(--color-primary-soft)' : 'var(--color-surface)',
                  borderBottom: '1px solid var(--color-border)',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: isToday ? 'var(--color-primary)' : 'var(--color-ink)' }}>
                    {isToday ? 'Today' : date} <span style={{ fontWeight: 400, color: 'var(--color-ink-faint)' }}>· {dayExpenses.length} entr{dayExpenses.length !== 1 ? 'ies' : 'y'}</span>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>₹{fmt(dayTotal)}</div>
                </div>
                <div>
                  {dayExpenses.map((e, i) => (
                    <div key={e._id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                      borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
                    }}>
                      {e.category === 'doctor_cost' && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 100, background: 'var(--color-warning-soft)', color: 'var(--color-warning)', flexShrink: 0 }}>
                          Doctor Cost
                        </span>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13 }}>{e.note}</span>
                        {e.patientUID && <span style={{ fontSize: 11, color: 'var(--color-ink-faint)' }}> · {e.patientUID}</span>}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--color-ink-faint)' }}>
                        {e.method}{e.addedBy ? ` · by ${e.addedBy}` : ''}
                        {e.createdAt ? ` · ${new Date(e.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                      </span>
                      <span style={{ fontWeight: 700, color: 'var(--color-danger)', minWidth: 70, textAlign: 'right' }}>₹{fmt(e.amount)}</span>
                      <button className="btn btn-icon btn-ghost" onClick={() => setDeleteTarget(e)} title="Delete">
                        <Trash2 size={14} color="var(--color-danger)" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this expense?"
        message={deleteTarget ? `"${deleteTarget.note}" — ₹${fmt(deleteTarget.amount)}. This cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)}
      />
    </AppLayout>
  );
}
